import { useState } from "react";
import { NavLink } from "react-router-dom";

import { PillNavMenu } from "./PillNavMenu";
import { usePillNavScroll } from "./usePillNavScroll";
import { MOBILE_PILL_ITEMS } from "@/lib/moduleManifests";
import { renderModuleIcon } from "@/components/ui/navIcon";
import { cn } from "@/lib/utils/cn";

/**
 * `PillNav` — the mobile navigation. W42 F26/F35: **one island, four slots**
 * (`ADR-0107`).
 *
 * ```
 *   [ 298 × 60: 3 × 52px route tabs  ·  ⋯ menu ]   ← 4 equal zones
 * ```
 *
 * ### What this supersedes, and why the reasoning changed
 *
 * - W38.1 `ADR-0076` — three islands, 322 × 66: avatar 60 + pill 178 + menu 60.
 * - `ADR-0019` — one pill holding 5 tabs plus a `⋯`.
 * - `ADR-0026` — a 5+4 positional split.
 *
 * The avatar moved to the topbar in F28, and **that dissolves ADR-0019's
 * constraint rather than contradicting it**. ADR-0019 deleted `SubRowNav`
 * because "pair / profile / settings rendered twice", and made the left island a
 * 60px circle for the sole purpose of being the only route to `/profile`. With
 * the avatar global, that constraint holds by construction, so the left island
 * has no reason to exist and `⋯` folds into the pill as a fourth slot. This is
 * re-argued in ADR-0108 rather than inherited silently, because the old ADR's
 * *conclusion* (「拒絕參考圖的單條 pill 導航」) is the opposite of what ships.
 *
 * ### Why the avatar island going away was not optional
 *
 * It was a control that did nothing. Every one of the 9 modules is still
 * reachable: 3 tabs, 5 menu rows, and `/profile` from the topbar avatar. What
 * disappeared is a duplicate, and a duplicate entry is a maintenance cost that
 * the three-island arithmetic made worse — every one of those forms had to be
 * reconciled to a shared height and re-reconciled at each breakpoint.
 *
 * ### Why the width is 298 and not 216
 *
 * Measured, not chosen. The reference's navy bar spans **76.4%** of its screen;
 * the 216px version measured **55.5%** of 390px, leaving 87px of dead wash per
 * side, which is why it read as a floating lozenge rather than a nav bar. The
 * extra width is distributed to the **tabs** (`flex: 1 1 0`) rather than to the
 * gaps — see `.nav-pill` / `.nav-item` in `globals.css` for the arithmetic and
 * the reason a gap-only change was rejected. Re-measured at **75.8%**.
 *
 * Compatibility surface other tests and `DESIGN.md §13` depend on: the
 * `data-testid="pill-nav"`, `data-component="PillNav"`, `data-scrolled`, the
 * `safe-bottom` utility, and `MobileShell`'s bottom clearance all stay.
 */
export function PillNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrolled } = usePillNavScroll();
  // Nothing profile-related is read here any more: the avatar belongs to the
  // topbar in F28, and `useLocation` is not needed either because each tab's
  // active state comes from `NavLink`'s own render prop.

  return (
    <>
      {/* Scrim + outside-click catcher, only armed while the menu is open. It
          is a sibling of <nav> rather than a child so the nav keeps its own
          stacking context: the trigger must stay clickable to toggle closed. */}
      <div
        aria-hidden
        data-open={menuOpen ? "true" : undefined}
        onClick={() => setMenuOpen(false)}
        className={cn(
          "pointer-events-none fixed inset-0 z-20 bg-black/0",
          "transition-colors duration-base ease-quiet",
          menuOpen && "pointer-events-auto bg-black/18",
        )}
      />

      <nav
        data-component="PillNav"
        data-testid="pill-nav"
        aria-label="行動導航"
        // One scroll state for the whole shell (ADR-0111): content passing
        // underneath thickens the chrome. W40.1 F3 removed `data-minimized` and
        // the `data-minimize-on-scroll` opt-out hook ADR-0019 had reserved for
        // exactly this behaviour — nothing ever read the hook, and the behaviour
        // it reserved for is withdrawn (ADR-0100).
        data-scrolled={scrolled ? "true" : "false"}
        // `.nav-pill-host` owns the fixed positioning and the bottom offset.
        // W45.0: it used to be `safe-bottom fixed bottom-4` here, and
        // `safe-bottom` is `padding-bottom: env(safe-area-inset-bottom)` — so
        // the two stacked and the pill sat ~64px above the screen edge on a
        // ColorOS gesture device. The class is gone; the arithmetic is in
        // `globals.css` next to the geometry it belongs to.
        className="nav-pill-host"
      >
        <div className="nav-pill">
          {MOBILE_PILL_ITEMS.map(({ to, icon, label, testId }) => (
            <NavLink
              key={to}
              to={to}
              aria-label={label}
              data-testid={`${testId}-mobile`}
              className={({ isActive }) =>
                cn(
                  // `nav-item` owns the 48px box and the `flex: 1 1 0` share, in
                  // globals.css — a `w-[52px]` utility here would be an
                  // unlayered-vs-layered conflict and the fluid width would
                  // never apply. `min-h-11` is kept so the class string itself is
                  // the 44pt evidence `expectTouchTarget` reads (jsdom has no
                  // layout engine, `tests/viewport.ts`).
                  "nav-item",
                  "tap-target",
                  "transition-[transform,box-shadow,background-color] duration-quick ease-paper",
                  "active:scale-95",
                  // W45.1 `ADR-0112`: the selected state is `.nav-item-on`, and
                  // the *fill* rides `.nav-slot` — a 44×44 box enclosing the
                  // icon and the label. It is a new element precisely so the
                  // painted shape and the 48px touch target stop being the same
                  // object; see `.nav-slot` in globals.css.
                  isActive ? "nav-item-on" : "text-ink-3",
                )
              }
            >
              {({ isActive }) => (
                <span className="nav-slot">
                  {renderModuleIcon(icon, { className: "icon-md nav-tile" })}
                  <span aria-hidden data-active={isActive ? "true" : undefined}>
                    {label}
                  </span>
                </span>
              )}
            </NavLink>
          ))}
          <PillNavMenu open={menuOpen} onOpenChange={setMenuOpen} />
        </div>
      </nav>
    </>
  );
}
